import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { Check, Sparkles, Crown, Star, Bell, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TIERS = [
  {
    id: "bronze", name: "Bronze", icon: Bell, price: 19, accent: "from-amber-700 to-amber-500",
    tagline: "Stay in the loop.",
    features: [
      "Weekly best-deal email digest",
      "Lease deal alerts by brand",
      "Access to journal premium articles",
      "Annual lease-end checklist",
    ],
  },
  {
    id: "silver", name: "Silver", icon: Star, price: 49, accent: "from-zinc-400 to-zinc-200",
    tagline: "Make better decisions.",
    featured: true,
    features: [
      "Everything in Bronze",
      "Quarterly broker check-in call (15 min)",
      "Trade-in valuation refresh anytime",
      "Priority email response",
      "AI Negotiation Assistant credits (10/yr)",
    ],
  },
  {
    id: "gold", name: "Gold", icon: Crown, price: 149, accent: "from-yellow-500 to-amber-400",
    tagline: "Full concierge access.",
    features: [
      "Everything in Silver",
      "Unlimited broker calls",
      "Personalized monthly vehicle strategy",
      "First access to off-market sourcing",
      "Discounted broker fee on purchases (-25%)",
      "Concierge text line",
    ],
  },
];

export default function Concierge() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [statusKind, setStatusKind] = useState(""); // success | pending | error | cancelled
  const polledRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
  }, [user]);

  // Handle return-from-stripe polling
  useEffect(() => {
    const status = params.get("status");
    const sessionId = params.get("session_id");
    if (status === "cancelled") {
      setStatusKind("cancelled");
      setStatusMsg("Checkout cancelled. No charges were made.");
      return;
    }
    if (status === "success" && sessionId && !polledRef.current) {
      polledRef.current = true;
      setStatusKind("pending");
      setStatusMsg("Confirming your payment…");
      pollStatus(sessionId, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollStatus = async (sessionId, attempts) => {
    if (attempts >= 6) {
      setStatusKind("error");
      setStatusMsg("We didn't get confirmation in time. Check your email or contact support.");
      return;
    }
    try {
      const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatusKind("success");
        setStatusMsg(`Welcome to ${data.tier.toUpperCase()}. Your membership is active for 30 days.`);
        const me = await api.get("/billing/me");
        setBilling(me.data);
        return;
      }
      if (data.status === "expired") {
        setStatusKind("error");
        setStatusMsg("Checkout session expired. Please try again.");
        return;
      }
      setStatusMsg("Confirming your payment…");
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2500);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2500);
    }
  };

  const subscribe = async (tier) => {
    if (!user) {
      toast.error("Please sign in to subscribe");
      window.location.href = `/login?next=/concierge`;
      return;
    }
    setBusy(tier);
    try {
      const { data } = await api.post("/billing/checkout", {
        tier,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      setBusy(null);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16" data-testid="concierge-page">
      {statusMsg && (
        <div
          className={`mb-8 rounded-2xl px-5 py-4 border flex items-center gap-3 animate-fade-up ${
            statusKind === "success" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
            : statusKind === "error" ? "border-red-500/40 bg-red-500/10 text-red-200"
            : statusKind === "cancelled" ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-200"
            : "border-white/15 bg-white/5 text-gray-200"
          }`}
          data-testid="billing-status"
        >
          {statusKind === "success" ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="text-sm">{statusMsg}</span>
        </div>
      )}

      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-5"><Sparkles className="w-3 h-3 text-silver" /><span className="label-mono">555 Concierge Membership</span></div>
        <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-5">Always on. Always advised.</h1>
        <p className="text-gray-400">30-day access pass. Use it during your active vehicle search, then renew (or don't). No hidden recurring charges.</p>
        {billing?.tier && billing?.status === "active" && (
          <div className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full silver-gradient text-[#050507] text-xs font-bold">
            ACTIVE: {billing.tier.toUpperCase()} · expires {billing.expires_at ? new Date(billing.expires_at).toLocaleDateString() : "—"}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-5 mb-14">
        {TIERS.map((t) => {
          const isCurrent = billing?.tier === t.id && billing?.status === "active";
          return (
            <div
              key={t.id}
              data-testid={`concierge-tier-${t.id}`}
              className={`relative rounded-2xl p-8 border ${t.featured ? "bg-gradient-to-b from-white/[0.08] to-transparent border-white/20" : "bg-[#0A0A0E] border-[#1A1A24]"}`}
            >
              {t.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full silver-gradient text-[#050507] text-[10px] font-black tracking-widest">MOST POPULAR</div>}
              <t.icon className={`w-7 h-7 mb-5 bg-gradient-to-br ${t.accent} bg-clip-text text-transparent`} strokeWidth={1.5} />
              <div className="font-display text-3xl mb-1">{t.name}</div>
              <p className="text-gray-400 text-sm mb-6">{t.tagline}</p>
              <div className="mb-6">
                <span className="font-display text-5xl font-light">${t.price}</span>
                <span className="text-gray-400 text-sm ml-1">/ 30 days</span>
              </div>
              <ul className="space-y-3 mb-8">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                    <Check className="w-4 h-4 text-silver flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe(t.id)}
                disabled={busy === t.id || isCurrent}
                data-testid={`concierge-subscribe-${t.id}`}
                className={`w-full py-3 rounded-full font-bold text-sm transition disabled:opacity-50 ${
                  isCurrent ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-300"
                  : t.featured ? "silver-gradient text-[#050507]"
                  : "border border-white/20 text-white hover:bg-white/5"
                }`}
              >
                {isCurrent ? "Current Plan" : busy === t.id ? "Loading…" : `Get ${t.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-gray-500 max-w-xl mx-auto">
        Powered by Stripe (test mode). One-time 30-day access pass — no auto-renewal. Cancel any time by simply not renewing.
      </p>
    </div>
  );
}
